import type { Product } from '@skop-app/lib-domain/types'
import { matchLabel } from '@skop-app/lib-domain/utils'
import { useMemo } from 'react'

import {
  buildCanonicalOrderProduct,
  buildOrderModalItemFromProduct,
} from '@/features/quote/lib/orderProducts'
import { useInventoryProducts } from '@/services/product'
import type { OrderModalValues } from '@/types'

type OrderItem = NonNullable<OrderModalValues['items']>[number]

type ItemWithNature = OrderItem & {
  nature?: string
  rawProduct?: {
    nature?: string
  } | null
}

type GroupedProduct = Product & {
  actionLabels?: string[]
}

type ProductGroup = {
  mainProduct: Product
  totalQuantity: number
  actionValues: Set<string>
}

const getOrderItemNature = (item: OrderItem): string | undefined => {
  const itemWithNature = item as ItemWithNature
  return itemWithNature.nature ?? itemWithNature.rawProduct?.nature
}

const getProductQuantity = (product: Product): number =>
  Number(product.quantity?.number ?? 0) || 0

const getProductActionValue = (product: Product): string | undefined => {
  const action = product.action as unknown

  if (typeof action === 'string') return action
  if (!action || typeof action !== 'object' || !('value' in action))
    return undefined

  const value = (action as { value?: unknown }).value
  return typeof value === 'string' ? value : undefined
}

const getActionLabels = (actionValues: Set<string>): string[] => {
  const labels = [...actionValues]
    .map((actionValue) => {
      const label = matchLabel(actionValue, 'action').label
      return typeof label === 'string' && label.length > 0 ? label : null
    })
    .filter((label): label is string => Boolean(label))

  return [...new Set(labels)]
}

export const sortOrderItemsWithServicesLast = (
  items: OrderModalValues['items'],
): OrderModalValues['items'] => {
  if (!items?.length) return items

  return [...items].sort((firstItem, secondItem) => {
    const isFirstService = getOrderItemNature(firstItem) === 'service'
    const isSecondService = getOrderItemNature(secondItem) === 'service'

    if (isFirstService === isSecondService) return 0
    return isFirstService ? 1 : -1
  })
}

export const toOrderItem = (product: Product): OrderItem => {
  const rawQuantity = product.quantity?.number ?? 0
  const quantity =
    product.nature === 'service' ? Math.max(1, rawQuantity) : rawQuantity
  const canonicalProduct = buildCanonicalOrderProduct({
    source: product as unknown as Record<string, unknown>,
    quantityNumber: quantity,
    quantityUnit: product.quantity?.unit ?? 'unit',
    priceAmount: product.price?.amount ?? 0,
    priceCurrency: product.price?.currency ?? 'eur',
  })
  const orderItem = buildOrderModalItemFromProduct({
    product: canonicalProduct,
    fallbackByUserID: product.byUserID,
  })

  return {
    ...orderItem,
    unitLabel: matchLabel(orderItem.unit, 'unit').shortLabel,
  }
}

export const useProductsGrouped = () => {
  const { products: rawProducts = [], isLoading } = useInventoryProducts({
    filters: {
      progress: { eq: 'active' },
      type: { ne: 'done' },
      action: { ne: 'reserved' },
      nature: { ne: 'service' },
    },
  })

  const products = useMemo(() => {
    const groupedProducts = new Map<string, ProductGroup>()

    for (const product of rawProducts) {
      const productID = product.productID
      if (!productID) continue

      const quantity = getProductQuantity(product)
      const actionValue = getProductActionValue(product)
      const existingGroup = groupedProducts.get(productID)

      if (!existingGroup) {
        groupedProducts.set(productID, {
          mainProduct: product,
          totalQuantity: quantity,
          actionValues: new Set(actionValue ? [actionValue] : []),
        })
        continue
      }

      existingGroup.totalQuantity += quantity
      if (actionValue) existingGroup.actionValues.add(actionValue)
    }

    return Array.from(groupedProducts.values())
      .map(({ mainProduct, totalQuantity, actionValues }) => {
        const actionLabels = getActionLabels(actionValues)

        return {
          ...mainProduct,
          quantity: {
            ...(mainProduct.quantity ?? {}),
            number: totalQuantity,
          },
          ...(actionLabels.length > 0 ? { actionLabels } : {}),
        } as GroupedProduct
      })
      .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
  }, [rawProducts])

  const productIdToMaxQuantity = useMemo(() => {
    const map = new Map<string, number>()
    for (const product of products) {
      if (!product.productID) continue
      map.set(product.productID, getProductQuantity(product))
    }
    return map
  }, [products])

  return { products, productIdToMaxQuantity, isLoading }
}
